'use client'

import classNames from 'classnames'
import styles from './Popup.module.css'
import {
  Alignment,
  ElementSize,
  type FocusAndPointerHandler,
  type PopupContainerProps,
  PopupPosition,
  PopupProps,
  Position,
  TriggerRectangle,
} from './types'
import {
  type EventHandler,
  type FocusEventHandler,
  type JSX,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  POPUP_CONTAINER_ATTR,
  POPUP_TRIGGER_ATTR,
  findFirstFocusableChild,
  findFocusableChildren,
  getAriaDescribedBy,
  getAriaRole,
  getScrollParent,
  isElementTriggerWithinTarget,
} from './helpers'
import { PopupContext, usePopupContext } from './PopupContext'
import { createPortal } from 'react-dom'

// eslint-disable-next-line import/exports-last

function isEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

/**
 * Returns a function that sets the state to the given value if it's different from the old value.
 */
function setStateFn<T extends Record<string, unknown> | null>(value: T) {
  return (old: T) => {
    if (old === value) return old
    if (!old) return value
    if (isEqual(old, value)) return old
    return value
  }
}

// eslint-disable-next-line import/exports-last
export const Keys = {
  Enter: 'Enter',
  Space: ' ',
  Tab: 'Tab',
  Escape: 'Escape',
  Down: 'ArrowDown',
  Up: 'ArrowUp',
  Left: 'ArrowLeft',
  Right: 'ArrowRight',
  Home: 'Home',
  End: 'End',
} as const

function getPopupTarget() {
  const target = document.getElementById('bp-portal-target')
  if (target) return target

  const popupTarget = document.createElement('div')
  popupTarget.id = 'bp-portal-target'
  popupTarget.style.position = 'absolute'
  popupTarget.setAttribute('role', 'popup')
  document.body.appendChild(popupTarget)
  return popupTarget
}
// eslint-disable-next-line import/exports-last
export type MaybeHtml = HTMLElement | null | undefined
function extractElementSize(rect?: DOMRect | null): ElementSize | null {
  if (!rect) return null
  const { height, width } = rect
  return {
    height,
    width,
  }
}

function PopupContainer({ triggerRect, id, onCloseTransitionEnd, onPopupLeave, children }: PopupContainerProps) {
  const {
    popupRef: ref,
    spyRef,
    preferredPopupPosition,
    alignPopup,
    hasHoverTrigger,
    Anchor,
    contentClassName,
    contentWrapperClassName,
    popupOpenWrapperClassName,
    anchorClassName,
    marginInPx,
  } = usePopupContext()
  const [popupSize, setPopupSize] = useState<ElementSize | null>(null)
  const [bodyWidth, setBodyWidth] = useState<number | null>(null)
  const [bodyHeight, setBodyHeight] = useState<number | null>(null)
  const [cssOpen, setCssOpen] = useState(false)
  const { open, hidePopup, popupRef } = usePopupContext()

  // We need a small delay for transitions to be enabled
  useEffect(() => {
    if (!open) return setCssOpen(false)
    const frame = window.requestAnimationFrame(() => {
      setCssOpen(true)
    })
    return () => window.cancelAnimationFrame(frame)
  }, [open, setCssOpen])

  const extractPopupSize = useCallback(() => {
    const size = extractElementSize(ref?.current?.getBoundingClientRect())
    setPopupSize(setStateFn(size))
  }, [ref])

  const extractBodySize = useCallback(() => {
    const scrollContainer = getScrollParent(getPopupTarget()) ?? document.body
    const container = scrollContainer === window ? document.body : (scrollContainer as HTMLElement)
    const rect = container.getBoundingClientRect()
    setBodyWidth(rect.width)
    setBodyHeight(rect.height)
  }, [])

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== Keys.Escape) return
      event.preventDefault()
      const trigger = spyRef?.current?.nextElementSibling as MaybeHtml
      trigger?.focus()
      hidePopup()
    },
    [hidePopup, spyRef]
  )

  useEffect(() => {
    const ele = ref?.current
    if (!ele) return
    const focusableChildren = findFocusableChildren(ele)
    focusableChildren?.forEach((child, index) => {
      child.addEventListener('keydown', handleEscape)
    })
    return () => {
      focusableChildren?.forEach((child, index) => {
        child.removeEventListener('keydown', handleEscape)
      })
    }
  }, [children, handleEscape, ref])

  useLayoutEffect(() => {
    extractPopupSize()
    extractBodySize()
  }, [extractPopupSize, extractBodySize])

  const onWindowResize = useCallback(() => {
    window.requestAnimationFrame(() => {
      extractPopupSize()
      extractBodySize()
    })
  }, [extractPopupSize, extractBodySize])

  const onPointerLeave: EventHandler<ReactPointerEvent<HTMLElement>> = useCallback(
    (event) => {
      if (hasHoverTrigger) onPopupLeave(event)
    },
    [onPopupLeave, hasHoverTrigger]
  )

  const onFocusOut: FocusEventHandler<HTMLElement> = useCallback(
    (event) => {
      // tabindex=-1 container
      const focusableContainer = popupRef?.current?.firstElementChild?.firstElementChild
      if (event.target === focusableContainer) return
      if (ref?.current?.contains(event.relatedTarget)) return
      if (isElementTriggerWithinTarget(focusableContainer, event.relatedTarget)) {
        return event.preventDefault()
      }
      onPopupLeave(event)
    },
    [onPopupLeave, popupRef, ref]
  )
  useEffect(() => {
    window.addEventListener('resize', onWindowResize)
    return () => {
      window.removeEventListener('resize', onWindowResize)
    }
  }, [onWindowResize])

  const anchorSize = useMemo(() => (Anchor ? 17 : 0), [Anchor])
  useEffect(() => {
    if (open) return
    const ele = ref?.current
    // no animation trigger close immediately
    if (!popupOpenWrapperClassName) return onCloseTransitionEnd()
    ele?.addEventListener('transitionend', onCloseTransitionEnd)
    const timeout = setTimeout(onCloseTransitionEnd, 300)
    return () => {
      clearTimeout(timeout)
      ele?.removeEventListener('transitionend', onCloseTransitionEnd)
    }
  }, [ref, open, onCloseTransitionEnd, popupOpenWrapperClassName])

  const {
    x,
    y,
    anchorOffsetX: arrowPopupOffsetX,
    position,
    width,
  } = useMemo(
    () =>
      calculatePopupPosition({
        triggerRect,
        popupSize,
        bodyWidth,
        bodyHeight,
        anchorSize,
        preferredPopupPosition,
        alignPopup,
        originalMargin: marginInPx,
      }),
    [triggerRect, popupSize, bodyWidth, bodyHeight, anchorSize, preferredPopupPosition, alignPopup, marginInPx]
  )

  return createPortal(
    <div
      id={id}
      className={styles.popupContainer}
      ref={ref}
      onPointerLeave={onPointerLeave}
      onBlur={onFocusOut}
      style={{
        transform: `translate3d(${x}px, ${y}px, 0)`,
      }}
    >
      <div
        className={classNames(
          contentWrapperClassName,
          popupOpenWrapperClassName && { [popupOpenWrapperClassName]: cssOpen }
        )}
      >
        {Anchor && position === Position.BOTTOM && (
          <Anchor position={position} x={arrowPopupOffsetX} className={anchorClassName} />
        )}
        <div
          tabIndex={-1}
          className={contentClassName}
          style={{
            ...(alignPopup === Alignment.JUSTIFY ? { minWidth: width, overflowX: 'hidden' } : {}),
          }}
          {...{ [POPUP_CONTAINER_ATTR]: id }}
        >
          {children}
        </div>
        {Anchor && position === Position.TOP && (
          <Anchor position={position} x={arrowPopupOffsetX} className={anchorClassName} />
        )}
      </div>
    </div>,
    getPopupTarget()
  )
}

function useSetupTrigger(id: string, children: JSX.Element | undefined, spyRef?: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    if (!spyRef?.current) return
    const element = spyRef?.current?.nextElementSibling

    if (!element) return

    /**
      This is a workaround so that there's at least one referenced aria-describedby in the document before
      before the tooltip appears on the screen. Without this we would be violating an ARIA rule.
    **/
    spyRef.current.setAttribute('id', 'tooltip-spy')

    // When there is a focusable element nested we should find that and use it for better accessibility
    const focusableElement = findFirstFocusableChild(element)
    const targetElement = focusableElement ?? element
    element.setAttribute(POPUP_TRIGGER_ATTR, id)
    // We get the original explicit or implicit role or role description of the popup target
    const roleDescription = targetElement.getAttribute('aria-roledescription')
    const originalRole = roleDescription ?? getAriaRole(targetElement) ?? 'button'

    /**
     * When there is a focusable element we set the role to button, so all browsers can pick up the associated popup text
     * Browsers behave differently, so without this hack Chrome picks up the popup, FF and Safari does not. If we force Safari to pick it up
     * that breaks Chrome and FF.
     */
    targetElement.setAttribute('role', 'button')
    if (!focusableElement) {
      targetElement?.setAttribute('tabindex', '0')
      targetElement?.classList.add(styles.nonFocusableOutline!)
    }
    /**
     * After we set the role on the popup target we can set aria-roledescription to the original role,
     * so the screen reader can announce the role correctly.
     */
    targetElement.setAttribute('aria-roledescription', originalRole)

    const ariaIds = targetElement.getAttribute('aria-describedby')
    const ariaAlreadyIncludesId = ariaIds?.includes(id)

    // We make sure that the popup and any additional descriptions are in place for the screen readers
    if (!ariaAlreadyIncludesId) {
      targetElement.setAttribute('aria-describedby', `${getAriaDescribedBy(ariaIds, id)} tooltip-spy`)
    }
  }, [children, id, spyRef])
}

function extractTriggerRectangle(
  rect: DOMRect | null | undefined,
  offsetY: number,
  offsetX: number
): TriggerRectangle | null {
  if (!rect) return null
  const { left, top, bottom, width } = rect
  const res = {
    left,
    top,
    bottom,
    width,
    offsetX,
    offsetY,
  }
  return res
}

type PopupContentProps = Pick<PopupProps, 'children' | 'content'> & { id: string }
function PopupContent({ children, content, id }: PopupContentProps) {
  const {
    showPopup,
    hidePopup,
    togglePopup,
    open,
    spyRef,
    popupRef,
    hasClickTrigger,
    hasFocusTrigger,
    hasHoverTrigger,
    hasKeypressTrigger,
    setInDOM,
  } = usePopupContext()

  const [triggerRect, setTriggerRect] = useState<TriggerRectangle | null>(null)

  const hasTriggerRect = useMemo(() => !!triggerRect, [triggerRect])

  useSetupTrigger(id, children, spyRef)

  const setTriggerRectangle = useCallback(() => {
    // we're assuming that the trigger has size and position
    const element = spyRef?.current?.nextSibling as HTMLElement | null
    let rect = element?.getBoundingClientRect()
    if (!rect?.height) rect = undefined
    let offsetY = 0
    let offsetX = 0
    // in the context of the popup overlay nested in a dialog we need to offset the results, by the position of the
    // container itself and then take scrolling into account
    const scrollContainer = getScrollParent(getPopupTarget()) ?? window
    if (scrollContainer !== window) {
      const normalContainer = scrollContainer as HTMLElement
      offsetY = -normalContainer.getBoundingClientRect().top + normalContainer.scrollTop
      offsetX = -normalContainer.getBoundingClientRect().left + normalContainer.scrollLeft
    }
    const next = extractTriggerRectangle(rect, offsetY, offsetX)
    setTriggerRect(setStateFn(next))
    setInDOM(true)
  }, [setInDOM, spyRef])

  const clearTriggerRectangle = useCallback(() => {
    setTriggerRect(null)
    setInDOM(false)
  }, [setInDOM])

  const updateTriggerRect = useCallback(() => {
    if (!hasTriggerRect) return
    setTriggerRectangle()
  }, [setTriggerRectangle, hasTriggerRect])

  const onTriggerLeave: FocusAndPointerHandler = useCallback(
    (event) => {
      const popup = popupRef?.current
      if (event.relatedTarget instanceof Node && popup?.contains(event.relatedTarget)) {
        return event.preventDefault()
      }
      hidePopup()
    },
    [hidePopup, popupRef]
  )

  const onPopupLeave: FocusAndPointerHandler = useCallback(
    (event) => {
      if (event.relatedTarget instanceof Node && spyRef?.current?.nextSibling?.contains(event.relatedTarget)) {
        return event.preventDefault()
      }
      hidePopup()
    },
    [hidePopup, spyRef]
  )

  const onClickOutsidePopup = useCallback(
    (event: PointerEvent | MouseEvent) => {
      const node = event.target instanceof Node
      if (node && popupRef?.current?.contains(event.target)) return
      if (node && spyRef?.current?.nextElementSibling?.contains(event.target)) return
      onPopupLeave(event)
    },
    [onPopupLeave, popupRef, spyRef]
  )

  const onTriggerKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === Keys.Enter || event.key === Keys.Space) {
        event.preventDefault()
        return togglePopup()
      }
      if (event.key === Keys.Escape) return hidePopup()
    },
    [hidePopup, togglePopup]
  )

  // on click outside
  useEffect(() => {
    if (!hasClickTrigger) return

    document.addEventListener('pointerdown', onClickOutsidePopup)
    return () => {
      document.removeEventListener('pointerdown', onClickOutsidePopup)
    }
  }, [hasClickTrigger, onClickOutsidePopup])

  // on click
  useEffect(() => {
    if (!hasClickTrigger) return

    const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml

    triggerElement?.addEventListener('pointerdown', togglePopup)
    return () => {
      triggerElement?.removeEventListener('pointerdown', togglePopup)
    }
  }, [hasClickTrigger, togglePopup, spyRef, children])

  // on hover
  useEffect(() => {
    if (!hasHoverTrigger) return

    const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml

    triggerElement?.addEventListener('mouseover', showPopup)
    triggerElement?.addEventListener('mouseleave', onTriggerLeave)

    return () => {
      // clearInterval(interval)
      triggerElement?.removeEventListener('mouseover', showPopup)
      triggerElement?.removeEventListener('mouseleave', onTriggerLeave)
    }
  }, [hasHoverTrigger, onTriggerLeave, showPopup, spyRef, children])

  // focusout is always there
  useEffect(() => {
    const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml

    triggerElement?.addEventListener('focusout', onTriggerLeave)
    return () => triggerElement?.removeEventListener('focusout', onTriggerLeave)
  }, [onTriggerLeave, spyRef, children])

  // on focus
  useEffect(() => {
    if (!hasFocusTrigger) return

    const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml

    triggerElement?.addEventListener('focus', showPopup)
    // focusin is necessary in cases like disabled radio buttons
    triggerElement?.addEventListener('focusin', showPopup)

    return () => {
      triggerElement?.removeEventListener('focus', showPopup)
      triggerElement?.removeEventListener('focusin', showPopup)
    }
  }, [hasFocusTrigger, showPopup, children, spyRef])

  // on keypress
  useEffect(() => {
    if (!hasKeypressTrigger) return
    const triggerElement = spyRef?.current?.nextElementSibling as MaybeHtml
    triggerElement?.addEventListener('keydown', onTriggerKeyDown)
    return () => {
      triggerElement?.removeEventListener('keydown', onTriggerKeyDown)
    }
  }, [hasKeypressTrigger, onTriggerKeyDown, spyRef, children])

  useEffect(() => {
    if (!open) return
    window.addEventListener('resize', updateTriggerRect, { passive: true })
    const scrollContainer = getScrollParent(getPopupTarget()) ?? window
    scrollContainer.addEventListener('scroll', updateTriggerRect, { passive: true })
    return () => {
      window.removeEventListener('resize', updateTriggerRect)
      scrollContainer.removeEventListener('scroll', updateTriggerRect)
    }
  }, [open, updateTriggerRect])

  // recalculate trigger whenever it changes
  useLayoutEffect(() => {
    updateTriggerRect()
  }, [children, updateTriggerRect])

  useEffect(() => {
    if (!open) return
    setTriggerRectangle()
  }, [open, setTriggerRectangle])

  return (
    <>
      <div ref={spyRef} className={styles.spyElement}></div>
      {children}
      {hasTriggerRect && (
        <PopupContainer
          triggerRect={triggerRect}
          onCloseTransitionEnd={clearTriggerRectangle}
          id={id}
          onPopupLeave={onPopupLeave}
        >
          {content}
        </PopupContainer>
      )}
    </>
  )
}
function Popup({
  children,
  content,
  id: externalId,
  triggerEvents = ['hover', 'focus'],
  inactive,
  ...props
}: PopupProps) {
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [inDOM, setInDOM] = useState(false)
  const popupRef = useRef<HTMLDivElement>(null)
  const spyRef = useRef<HTMLDivElement>(null)
  const parentContext = usePopupContext()
  const generatedId = useId()
  const id = useMemo(() => {
    return externalId ?? generatedId
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId])

  const hasClickTrigger = useMemo(() => triggerEvents?.includes('click'), [triggerEvents])
  const hasFocusTrigger = useMemo(() => triggerEvents?.includes('focus'), [triggerEvents])
  const hasHoverTrigger = useMemo(() => triggerEvents?.includes('hover'), [triggerEvents])
  const hasKeypressTrigger = useMemo(() => triggerEvents?.includes('keypress'), [triggerEvents])

  const hidePopup = useCallback(() => {
    setOpen(false)
  }, [])

  const showPopup = useCallback(() => {
    setOpen(true)
  }, [])

  const togglePopup = useCallback(() => {
    setOpen((old) => !old)
  }, [])

  useEffect(() => {
    setMounted(true)
    return () => {
      setMounted(false)
    }
  }, [])
  // do not render the popup if we're in SSR mode as useLayoutEffect is unavailable
  if (!mounted || inactive) return children

  return (
    <PopupContext.Provider
      value={{
        open,
        showPopup,
        hidePopup,
        togglePopup,
        spyRef,
        popupRef,
        hasClickTrigger,
        hasHoverTrigger,
        hasFocusTrigger,
        hasKeypressTrigger,
        inDOM,
        setInDOM,
        initialised: true,
        parentContext,
        ...props,
      }}
    >
      <PopupContent content={content} id={id}>
        {children}
      </PopupContent>
    </PopupContext.Provider>
  )
}

export function calculatePopupPosition({
  triggerRect,
  popupSize,
  bodyWidth,
  bodyHeight,
  originalMargin = 20,
  anchorSize = 17,
  preferredPopupPosition = Position.TOP,
  alignPopup = Alignment.CENTER,
}: {
  triggerRect: TriggerRectangle | null
  popupSize: ElementSize | null
  bodyWidth: number | null
  bodyHeight: number | null
  originalMargin?: number | undefined
  anchorSize?: number | undefined
  preferredPopupPosition?: Position
  alignPopup?: Alignment
}): PopupPosition {
  let x = 0
  let y = 0
  let right = 0
  let anchorOffsetX = 0
  let position: Position = preferredPopupPosition
  let width = popupSize?.width ?? 0
  if (!triggerRect || !popupSize || !bodyWidth || !bodyHeight)
    return { x, y, anchorOffsetX: anchorOffsetX, position, width }
  let origX = 0
  // reduce the margin if the popup is too wide to accommodate it
  let halfOfPopupWidth = popupSize.width / 2
  const halfOfTriggerWidth = triggerRect.width / 2
  if (alignPopup === Alignment.CENTER) {
    // center the popup in respect to the trigger, x refers to the left edge of the popup
    x = triggerRect.left + triggerRect.offsetX - halfOfPopupWidth + halfOfTriggerWidth
  } else if (alignPopup === Alignment.LEFT || alignPopup === Alignment.JUSTIFY) {
    x = triggerRect.left + triggerRect.offsetX
    if (alignPopup === Alignment.JUSTIFY) {
      width = triggerRect.width
      halfOfPopupWidth = halfOfTriggerWidth
    }
  } else if (alignPopup === Alignment.RIGHT) {
    x = triggerRect.left + triggerRect.offsetX + triggerRect.width - width
  }

  const margin = popupSize.width > bodyWidth ? 0 : Math.min(originalMargin, (bodyWidth - width) / 2)
  origX = x
  right = x + popupSize.width

  // popup overflows body width
  if (x < margin) {
    // to far too the left, let's put it on at the edge of the body
    x = margin
  } else if (right > bodyWidth - margin) {
    // to far too the right, we move it back to the left by the value of the overflow
    x -= right - (bodyWidth - margin)
  }

  if (anchorSize) {
    // arrow position should be centered in respect to the trigger.
    // The provided value is calculated in respect to the popup x position
    anchorOffsetX = origX - x + halfOfPopupWidth - anchorSize

    // Arrow should not be detached from the popup, eat up the margin if necessary
    const arrowOffsetOverflow = anchorOffsetX + 2 * anchorSize - width
    if (arrowOffsetOverflow > 0) {
      const offset = Math.min(arrowOffsetOverflow, margin)
      x += offset
      anchorOffsetX -= offset
    }
  }
  if (preferredPopupPosition === Position.TOP) {
    // put the popup above the trigger
    y = triggerRect.top + triggerRect.offsetY - popupSize.height

    if (y < Math.abs(triggerRect.offsetY)) {
      // It doesn't fit above, so place below.
      position = Position.BOTTOM
      y = triggerRect.bottom
    }
  } else {
    y = triggerRect.bottom + triggerRect.offsetY
    if (y + popupSize.height > bodyHeight + triggerRect.offsetY) {
      position = Position.TOP
      y = triggerRect.top + triggerRect.offsetY - popupSize.height
    }
  }
  return { x, y, anchorOffsetX, position, width }
}

export default Popup
